import React, { useState, useEffect } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import { useQuery } from '@apollo/client';
import { isEmpty } from 'lodash';

import { DromoUploader, getTranslatedText } from '@nile/components';
import { AccountClient, GET_SERVICE_ITEMS } from '@nile/graphql';
import Loader from '@nile/mangifera/src/Components/Ticket/TicketCount';

import {
  getClientContractImportFields,
  getQuery,
  callQuery,
  shapeOptions
} from '../Fields/clientContractFields';
import { intlConverter } from '../utils/helpers';

const ClientContractUploader = ({
  onResults,
  onCancel,
  validationProps = {},
  fields: propFields,
  userToken,
  intl,
  activator
}) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [fields, setFields] = useState([]);
  const [allServiceItems, setAllServiceItems] = useState([]);

  useQuery(GET_SERVICE_ITEMS, {
    client: AccountClient,
    variables: {
      listInfo: {}
    },
    onCompleted(res) {
      if (res?.getServiceItems?.items) {
        setAllServiceItems(res.getServiceItems.items);
      }
    }
  });

  function handleRowHooks({ row, index }) {
    const newRowValue = { row, index };
    const validationFieldsArray = [
      'applicableIn',
      'unitType',
      'itemQuantity',
      'frequencyUnit',
      'frequencyType',
      'frequencyQuantity'
    ];
    validationFieldsArray.forEach((key) => {
      newRowValue.row[key].info = [];
    });

    function addRequiredError(key) {
      newRowValue.row[key].info = [
        {
          message: getTranslatedText(intl, 'dromo.validation.requiredField'),
          level: 'error'
        }
      ];
    }

    const serviceItem = allServiceItems.find(
      (serItem) =>
        serItem.itemId === newRowValue.row?.subscriptionItemId?.resultValue
    );

    if (serviceItem) {
      if (
        serviceItem.quantityType === 'BLOCK_HOURS' &&
        isEmpty(newRowValue.row.applicableIn.resultValue)
      ) {
        addRequiredError('applicableIn');
      }
      if (
        serviceItem.quantityType === 'UNITS' &&
        isEmpty(newRowValue.row.unitType.resultValue)
      ) {
        addRequiredError('unitType');
      }

      if (
        serviceItem.quantityType !== 'UNITS' ||
        (serviceItem.quantityType === 'UNITS' &&
          newRowValue.row.unitType.resultValue === 'CUSTOM')
      ) {
        if (isEmpty(newRowValue.row.itemQuantity.resultValue)) {
          addRequiredError('itemQuantity');
        }
      }

      if (
        (serviceItem.quantityType === 'UNITS' ||
          serviceItem.quantityType === 'HOURS') &&
        newRowValue.row.frequencyType.resultValue === 'PERPETUAL'
      ) {
        newRowValue.row.frequencyType.info = [
          {
            message: getTranslatedText(
              intl,
              'dromo.validation.frequencyType.invalidOption'
            ),
            level: 'error'
          }
        ];
      }

      if (
        (serviceItem.quantityType === 'BLOCK_HOURS' ||
          serviceItem.quantityType === 'BLOCK_MONEY') &&
        newRowValue.row.frequencyType.resultValue === 'ONCE'
      ) {
        newRowValue.row.frequencyType.info = [
          {
            message: getTranslatedText(
              intl,
              'dromo.validation.frequencyType.invalidOption'
            ),
            level: 'error'
          }
        ];
      }
    }

    if (
      newRowValue.row.frequencyType.resultValue === 'CUSTOM' &&
      isEmpty(newRowValue.row.frequencyUnit.resultValue)
    ) {
      addRequiredError('frequencyUnit');
    }

    if (
      newRowValue.row.frequencyType.resultValue === 'CUSTOM' &&
      isEmpty(newRowValue.row.frequencyQuantity.resultValue)
    ) {
      addRequiredError('frequencyQuantity');
    }

    const frequencyQuantityMap = {
      DAY: 31,
      MONTH: 12,
      WEEK: 4,
      YEAR: 31
    };

    if (
      newRowValue.row.frequencyQuantity.resultValue >
      frequencyQuantityMap[newRowValue.row.frequencyUnit.resultValue]
    ) {
      newRowValue.row.frequencyQuantity.info = [
        {
          message: getTranslatedText(
            intl,
            'dromo.validation.frequencyQuantity.invalidOption'
          ),
          level: 'error'
        }
      ];
    }

    return newRowValue;
  }

  const handleReviewStep = (instance, data) => {
    const availableFields = Object.keys(data?.fields);

    const fieldsToAdd = getClientContractImportFields?.filter(
      (field) =>
        !('validators' in field) && !availableFields.includes(field.key)
    );
    fieldsToAdd.forEach((field) => {
      instance.addField({ ...field, type: field?.type || 'string' });
    });
  };

  useEffect(() => {
    if (!propFields) {
      setLoading(true);
      const initFields = [...getClientContractImportFields];

      const selectFields = getClientContractImportFields.reduce(
        (acc, field, idx) => {
          if (field.type === 'select')
            acc[idx] = getQuery(field.key)
              ? callQuery(field.key)
              : field.selectOptions;
          return acc;
        },
        {}
      );

      const promises = Object.values(selectFields);
      Promise.all(promises)
        .then((options) => {
          options.forEach((set, idx) => {
            const indexToModify = Object.keys(selectFields)[idx];
            initFields[indexToModify].selectOptions = shapeOptions(
              initFields[indexToModify].key,
              set
            );
          });
          setFields([...intlConverter(intl, initFields)]);
          setLoading(false);
        })
        .catch((e) => {
          setLoading(false);
          setError(true);
          console.log(e);
        });
    }
  }, []);

  if (loading) return <Loader loading />;

  if (error) return null;

  return (
    <DromoUploader
      settings={{
        importIdentifier: 'SUBSCRIPTION_ITEM',
        title: getTranslatedText(intl, 'dromo.clientContract.import.title')
      }}
      fields={propFields || fields}
      rowHooks={[(row) => row, handleRowHooks]}
      columnHooks={[(column) => column]}
      stepHooks={[{ type: 'reviewStep', callback: handleReviewStep }]}
      onResults={(response, metadata) => {
        setResults(response);
        if (onResults) onResults();
      }}
      onCancel={() => {
        if (onCancel) onCancel();
      }}
      activator={
        activator || (
          <div className="flex center actionListItemPadding ptr">
            <div className="mr-10 flex center">
              <SVGIcon name="Requesters" />
            </div>
            <FormattedMessage id="dromo.csv.clientContract.import" />
          </div>
        )
      }
      userToken={userToken}
      backendSync
    />
  );
};

export default injectIntl(ClientContractUploader);
